import { z } from 'zod';
import { sessionStorageKeys } from '@/constants/sessionStorageKeys';

const utmTagDataSchema = z.object({
  utm_campaign: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_term: z.string().optional(),
  utm_content: z.string().optional(),
  utm_custom_tags: z.record(z.string(), z.string()).optional(),
});

export type UtmTagData = z.infer<typeof utmTagDataSchema>;

const storePurchaseTrackingId = (purchaseTrackingId: string) => {
  sessionStorage.setItem(sessionStorageKeys.purchaseTrackingId, purchaseTrackingId);
};

const getPurchaseTrackingId = () =>
  sessionStorage.getItem(sessionStorageKeys.purchaseTrackingId) ?? undefined;

const clearPurchaseTrackingId = () => {
  sessionStorage.removeItem(sessionStorageKeys.purchaseTrackingId);
};

const storeUtmTags = (utmTags: UtmTagData) => {
  sessionStorage.setItem(sessionStorageKeys.utmTags, JSON.stringify(utmTags));
};

const getUtmTags = () => {
  const stringifiedTags = sessionStorage.getItem(sessionStorageKeys.utmTags);

  if (!stringifiedTags) {
    return undefined;
  }

  try {
    return utmTagDataSchema.parse(JSON.parse(stringifiedTags));
  } catch {
    return undefined;
  }
};

const clearUtmTags = () => {
  sessionStorage.removeItem(sessionStorageKeys.utmTags);
};

const storeChannelName = (channelName: string) => {
  sessionStorage.setItem(sessionStorageKeys.channelName, channelName);
};

const getChannelName = (): string | undefined =>
  sessionStorage.getItem(sessionStorageKeys.channelName) ?? undefined;

const clearChannelName = () => {
  sessionStorage.removeItem(sessionStorageKeys.channelName);
};

export const getTrackingData = () => ({
  purchaseTrackingId: getPurchaseTrackingId(),
  utmTags: getUtmTags(),
  channelName: getChannelName(),
});

export const storeTrackingData = ({
  purchaseTrackingId,
  utmTags,
  channelName,
}: {
  purchaseTrackingId?: string;
  utmTags?: UtmTagData;
  channelName?: string;
}) => {
  if (purchaseTrackingId) {
    storePurchaseTrackingId(purchaseTrackingId);
  }

  if (utmTags) {
    storeUtmTags(utmTags);
  }
  if (channelName) {
    storeChannelName(channelName);
  }
};

export const clearTrackingData = () => {
  clearPurchaseTrackingId();
  clearUtmTags();
  clearChannelName();
};
